import React from 'react'
import { Button, Tooltip } from 'antd'
import styled from 'styled-components/macro'
import { formatSuccessHubActivationTooltip } from 'src/utils/growthEngineSuccessHub'
import {
  SUCCESS_HUB_PROGRESS_TOKENS,
  successHubProgressSurfaceBorder,
} from 'src/utils/successHubProgressTokens'

const T = SUCCESS_HUB_PROGRESS_TOKENS

/**
 * Progress momentum: Kitchen on OMS % + climb line + bar.
 * When hideNextJob (Progress), drop repeating encouragement and “Next · …”
 * — today’s job card owns the next action. No stage badge ladder here.
 */
const SuccessHubMomentumStrip = ({
  momentum,
  onNextJob,
  hideNextJob = false,
  className,
  'data-testid': dataTestId = 'progress-momentum',
}) => {
  if (!momentum) return null
  const { encouragement, activation, nextJob, mature = false } = momentum
  if (!encouragement && !activation && !nextJob) {
    return null
  }

  const nextLabel = hideNextJob ? null : nextJob?.label || null
  const canClickNext = Boolean(nextLabel && onNextJob && nextJob?.cta?.action)
  // Climb line lives on the % hero — don’t repeat it as encouragement on Progress.
  const showEncouragement =
    Boolean(encouragement) && !(hideNextJob && activation)
  const displayPercent =
    activation?.displayPercent ??
    (activation?.ratio != null
      ? Math.round((activation.ratio || 0) * 100)
      : null)
  const activationTooltip = formatSuccessHubActivationTooltip(activation)

  return (
    <Strip
      className={className}
      data-testid={dataTestId}
      data-mature={mature ? 'true' : 'false'}
    >
      {activation ? (
        <Activation data-testid={`${dataTestId}-activation`}>
          <Tooltip title={activationTooltip} mouseEnterDelay={0.25}>
            <ActivationHero data-testid={`${dataTestId}-percent-hero`}>
              <Percent
                data-testid={`${dataTestId}-percent`}
                $mature={mature}
                aria-hidden={displayPercent == null ? undefined : 'true'}
              >
                {displayPercent != null ? `${displayPercent}%` : '—'}
              </Percent>
              <ActivationMeta>
                <ActivationName data-testid={`${dataTestId}-name`}>
                  complete
                </ActivationName>
                <ActivationHint data-testid={`${dataTestId}-hint`}>
                  {mature
                    ? 'Kitchen on OMS · core kitchen locked in'
                    : 'Kitchen on OMS · useful jobs here lift this'}
                </ActivationHint>
              </ActivationMeta>
            </ActivationHero>
          </Tooltip>
          <Tooltip title={activationTooltip} mouseEnterDelay={0.25}>
            <BarTrack>
              <BarFill
                style={{
                  width: `${
                    (activation.displayRatio ?? activation.ratio) * 100
                  }%`,
                }}
                role='progressbar'
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={displayPercent ?? 0}
                aria-label={activation.label}
                data-testid={`${dataTestId}-bar`}
              />
            </BarTrack>
          </Tooltip>
          <ActivationLabel data-testid={`${dataTestId}-label`}>
            {activation.label}
          </ActivationLabel>
        </Activation>
      ) : null}
      {showEncouragement ? (
        <Encouragement
          data-testid={`${dataTestId}-encouragement`}
          $mature={mature}
        >
          {encouragement}
        </Encouragement>
      ) : null}
      {nextLabel ? (
        canClickNext ? (
          <NextJobButton
            type='link'
            onClick={onNextJob}
            data-testid={`${dataTestId}-next-job`}
          >
            {nextLabel}
          </NextJobButton>
        ) : (
          <NextJob data-testid={`${dataTestId}-next-job`}>{nextLabel}</NextJob>
        )
      ) : null}
    </Strip>
  )
}

export default SuccessHubMomentumStrip

const Strip = styled.section`
  background: ${({ theme }) => theme?.colours?.mayoWhite || '#fff'};
  border: ${({ theme }) => successHubProgressSurfaceBorder(theme)};
  border-radius: ${T.radius.surface};
  display: flex;
  flex-direction: column;
  gap: ${T.space.sm};
  padding: ${T.pad.surface};
  width: 100%;
`

const Activation = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const ActivationHero = styled.div`
  align-items: baseline;
  cursor: help;
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
`

const Percent = styled.p`
  color: ${({ theme, $mature }) =>
    $mature
      ? theme?.colours?.seaweedGreen || '#132d07'
      : theme?.colours?.bbqBlack || '#000'};
  font-size: ${T.type.display};
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1;
  margin: 0;
`

const ActivationMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding-bottom: 2px;
`

const ActivationName = styled.p`
  color: ${({ theme }) => theme?.colours?.bbqBlack || '#000'};
  font-size: ${T.type.body};
  font-weight: 700;
  line-height: 1.3;
  margin: 0;
`

const ActivationHint = styled.p`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#595959'};
  font-size: ${T.type.lead};
  font-weight: 500;
  line-height: 1.35;
  margin: 0;
`

const BarTrack = styled.div`
  background: ${({ theme }) => theme?.colours?.paleGrey || '#ececec'};
  border-radius: 999px;
  cursor: help;
  height: 10px;
  overflow: hidden;
  width: 100%;
`

const BarFill = styled.div`
  background: ${({ theme }) => theme?.colours?.seaweedGreen || '#132d07'};
  border-radius: 999px;
  height: 100%;
  max-width: 100%;
  min-width: 0;
  transition: width 0.25s ease;
`

const ActivationLabel = styled.p`
  border: 0;
  clip: rect(0, 0, 0, 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`

const Encouragement = styled.p`
  color: ${({ theme }) => theme?.colours?.oliveGrey || '#595959'};
  font-size: ${T.type.lead};
  font-weight: 600;
  line-height: 1.45;
  margin: 0;
`

const NextJob = styled.p`
  color: ${({ theme }) => theme?.colours?.bbqBlack || '#000'};
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  margin: 0;
`

const NextJobButton = styled(Button)`
  && {
    align-self: flex-start;
    color: ${({ theme }) => theme?.colours?.seaweedGreen || '#132d07'};
    font-size: 13px;
    font-weight: 600;
    height: auto;
    line-height: 1.4;
    margin: 0;
    padding: 0;
  }
`
